// Types pour le module Assemblées Générales (AG)
// Basé sur le schéma de base de données actuel

import type { Database } from '@/shared/types/supabase'
import type { Enums } from '@/shared/types/database'

// ===== ENUMS =====
export type MeetingType = Enums<'meeting_type'>
export type MeetingStatus = Enums<'meeting_status'>
export type ConvocationStatus = Enums<'convocation_status'>
export type MajorityType = Enums<'majority_type'>
export type ResolutionCategory = Enums<'resolution_category'>
export type ResolutionStatus = Enums<'resolution_status'>
export type AnnexType = Enums<'annex_type'>
export type BureauRole = Enums<'bureau_role'>
export type ParticipationMode = Enums<'participation_mode'>
export type VoteMethod = Enums<'vote_method'>
// vote_type is stored as string in DB, not enum
export type VoteType = 'for' | 'against' | 'abstain'
export type CandidacyStatus = Enums<'candidacy_status'>
export type MandateEndReason = Enums<'mandate_end_reason'>
// council_position is stored as string in DB, not enum
export type CouncilPosition = 'PRESIDENT' | 'VICE_PRESIDENT' | 'SECRETARY' | 'TREASURER' | 'MEMBER'
// video_room_status - maintenant défini dans les types Supabase générés
export type VideoRoomStatus = Enums<'video_room_status'>

// ===== TYPES DE BASE =====

// Meetings (AG)
export type MeetingRow = Database['public']['Tables']['meetings']['Row']
export type MeetingInsert = Database['public']['Tables']['meetings']['Insert']
export type MeetingUpdate = Database['public']['Tables']['meetings']['Update']

// Resolutions
export type ResolutionRow = Database['public']['Tables']['resolutions']['Row']
export type ResolutionInsert = Database['public']['Tables']['resolutions']['Insert']
export type ResolutionUpdate = Database['public']['Tables']['resolutions']['Update']

// Meeting Participants
export type MeetingParticipantRow = Database['public']['Tables']['meeting_participants']['Row']
export type MeetingParticipantInsert = Database['public']['Tables']['meeting_participants']['Insert']
export type MeetingParticipantUpdate = Database['public']['Tables']['meeting_participants']['Update']

// Meeting Bureau
export type MeetingBureauRow = Database['public']['Tables']['meeting_bureau']['Row']
export type MeetingBureauInsert = Database['public']['Tables']['meeting_bureau']['Insert']
export type MeetingBureauUpdate = Database['public']['Tables']['meeting_bureau']['Update']

// Meeting Participant Units
export type MeetingParticipantUnitRow = Database['public']['Tables']['meeting_participant_units']['Row']
export type MeetingParticipantUnitInsert = Database['public']['Tables']['meeting_participant_units']['Insert']

// Correspondence Votes
export type CorrespondenceVoteRow = Database['public']['Tables']['correspondence_votes']['Row']
export type CorrespondenceVoteInsert = Database['public']['Tables']['correspondence_votes']['Insert']
export type CorrespondenceVoteUpdate = Database['public']['Tables']['correspondence_votes']['Update']

// Resolution Annexes
export type ResolutionAnnexRow = Database['public']['Tables']['resolution_annexes']['Row']
export type ResolutionAnnexInsert = Database['public']['Tables']['resolution_annexes']['Insert']

// Resolution Templates
export type ResolutionTemplateRow = Database['public']['Tables']['resolution_templates']['Row']
export type ResolutionTemplateInsert = Database['public']['Tables']['resolution_templates']['Insert']
export type ResolutionTemplateUpdate = Database['public']['Tables']['resolution_templates']['Update']

// Council Mandates
export type CouncilMandateRow = Database['public']['Tables']['council_mandates']['Row']
export type CouncilMandateInsert = Database['public']['Tables']['council_mandates']['Insert']
export type CouncilMandateUpdate = Database['public']['Tables']['council_mandates']['Update']

// Council Candidacies
export type CouncilCandidacyRow = Database['public']['Tables']['council_candidacies']['Row']
export type CouncilCandidacyInsert = Database['public']['Tables']['council_candidacies']['Insert']
export type CouncilCandidacyUpdate = Database['public']['Tables']['council_candidacies']['Update']

// Owner Votes
export type OwnerVoteRow = Database['public']['Tables']['owner_votes']['Row']
export type OwnerVoteInsert = Database['public']['Tables']['owner_votes']['Insert']
export type OwnerVoteUpdate = Database['public']['Tables']['owner_votes']['Update']

// Proxies
export type ProxyRow = Database['public']['Tables']['proxies']['Row']
export type ProxyInsert = Database['public']['Tables']['proxies']['Insert']
export type ProxyUpdate = Database['public']['Tables']['proxies']['Update']

// ===== TYPES ENRICHIS AVEC RELATIONS =====

export interface Meeting extends MeetingRow {
  residence?: {
    id: string
    name: string
    residence_key?: string | null
    address?: string | null
    city?: string | null
    postal_code?: string | null
    total_shares?: number | null
  } | null
  fiscal_year?: {
    id: string
    name: string
    start_date: string
    end_date: string
  } | null
  resolutions?: Resolution[] | { id: string }[]
  participants?: MeetingParticipant[] | { id: string }[]
  bureau?: MeetingBureau[]
}

export interface Resolution extends ResolutionRow {
  meeting?: {
    id: string
    title: string
    meeting_date: string
  } | null
  template?: {
    id: string
    code: string
    title: string
  } | null
  distribution_key?: {
    id: string
    name: string
  } | null
  budget?: {
    id: string
    name: string
  } | null
  supplier?: {
    id: string
    name: string
    code: string
  } | null
  annexes?: ResolutionAnnex[]
  votes?: OwnerVote[]
}

export interface MeetingParticipant extends MeetingParticipantRow {
  owner?: {
    id: string
    first_name: string
    last_name: string
  } | null
  proxy?: {
    id: string
    proxy_holder_name: string
  } | null
  correspondence_vote?: {
    id: string
    received_at: string
  } | null
  units?: MeetingParticipantUnit[]
}

export interface MeetingBureau extends MeetingBureauRow {
  owner?: {
    id: string
    first_name: string
    last_name: string
  } | null
}

export interface MeetingParticipantUnit extends MeetingParticipantUnitRow {
  unit?: {
    id: string
    unit_number: string
  } | null
  distribution_key?: {
    id: string
    name: string
  } | null
}

export interface CorrespondenceVote extends CorrespondenceVoteRow {
  owner?: {
    id: string
    first_name: string
    last_name: string
  } | null
  document?: {
    id: string
    display_name: string
    original_filename: string
  } | null
}

export interface ResolutionAnnex extends ResolutionAnnexRow {
  document?: {
    id: string
    display_name: string
    original_filename: string
  } | null
}

export interface ResolutionTemplate extends ResolutionTemplateRow {
  agency?: {
    id: string
    name: string
  } | null
}

export interface CouncilMandate extends CouncilMandateRow {
  owner?: {
    id: string
    first_name: string
    last_name: string
  } | null
  election_meeting?: {
    id: string
    title: string
    meeting_date: string
  } | null
  election_resolution?: {
    id: string
    title: string
  } | null
}

export interface CouncilCandidacy extends CouncilCandidacyRow {
  owner?: {
    id: string
    first_name: string
    last_name: string
  } | null
  meeting?: {
    id: string
    title: string
    meeting_date: string
  } | null
}

export interface OwnerVote extends OwnerVoteRow {
  owner?: {
    id: string
    first_name: string
    last_name: string
  } | null
  resolution?: {
    id: string
    title: string
  } | null
}

export interface Proxy extends ProxyRow {
  owner?: {
    id: string
    first_name: string
    last_name: string
  } | null
  proxy_holder_owner?: {
    id: string
    first_name: string
    last_name: string
  } | null
}

// ===== LABELS POUR L'UI =====

export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  ordinary: 'Ordinaire',
  extraordinary: 'Extraordinaire',
  mixed: 'Mixte',
}

// Note: Database enum values are SCHEDULED, CONVOCATION_READY, CONVOKED, LIVE, CLOSED
export const MEETING_STATUS_LABELS: Record<MeetingStatus, string> = {
  SCHEDULED: 'Planifiée',
  CONVOCATION_READY: 'Prête à convoquer',
  CONVOKED: 'Convoquée',
  LIVE: 'En cours',
  CLOSED: 'Clôturée',
}

export const MEETING_STATUS_COLORS: Record<MeetingStatus, string> = {
  SCHEDULED: 'blue',
  CONVOCATION_READY: 'purple',
  CONVOKED: 'cyan',
  LIVE: 'orange',
  CLOSED: 'green',
}

export const CONVOCATION_STATUS_LABELS: Record<ConvocationStatus, string> = {
  pending: 'En attente',
  generated: 'Générée',
  sent: 'Envoyée',
  delivered: 'Délivrée',
  failed: 'Échec',
}

export const CONVOCATION_STATUS_COLORS: Record<ConvocationStatus, string> = {
  pending: 'gray',
  generated: 'blue',
  sent: 'orange',
  delivered: 'green',
  failed: 'red',
}

export const MAJORITY_TYPE_LABELS: Record<MajorityType, string> = {
  simple: 'Majorité simple (art. 24)',
  absolute: 'Majorité absolue (art. 25)',
  absolute_25_1: 'Passerelle art. 25-1',
  double: 'Double majorité (art. 26)',
  unanimity: 'Unanimité',
}

export const MAJORITY_TYPE_DESCRIPTIONS: Record<MajorityType, string> = {
  simple: 'Majorité des voix exprimées des copropriétaires présents ou représentés',
  absolute: 'Majorité des voix de tous les copropriétaires (présents, représentés et absents)',
  absolute_25_1: 'Si le projet recueille 1/3 des voix, un second vote à la majorité simple est possible',
  double: 'Majorité des membres représentant au moins 2/3 des voix',
  unanimity: 'Accord de tous les copropriétaires',
}

export const RESOLUTION_CATEGORY_LABELS: Record<ResolutionCategory, string> = {
  approval_accounts: 'Approbation des comptes',
  approval_budget: 'Approbation du budget',
  works_maintenance: 'Travaux d\'entretien',
  works_improvement: 'Travaux d\'amélioration',
  works_major: 'Gros travaux',
  contract_renewal: 'Renouvellement de contrat',
  contract_new: 'Nouveau contrat',
  syndic_mandate: 'Mandat du syndic',
  council_election: 'Élection du conseil syndical',
  council_powers: 'Pouvoirs du conseil syndical',
  rules_modification: 'Modification du règlement',
  common_parts: 'Parties communes',
  miscellaneous: 'Divers',
}

// Note: Database enum values are DRAFT, VOTING, CLOSED
export const RESOLUTION_STATUS_LABELS: Record<ResolutionStatus, string> = {
  DRAFT: 'Brouillon',
  VOTING: 'Vote en cours',
  CLOSED: 'Clôturé',
}

export const RESOLUTION_STATUS_COLORS: Record<ResolutionStatus, string> = {
  DRAFT: 'gray',
  VOTING: 'orange',
  CLOSED: 'green',
}

export const BUREAU_ROLE_LABELS: Record<BureauRole, string> = {
  president: 'Président de séance',
  secretary: 'Secrétaire',
  scrutineer: 'Scrutateur',
}

export const PARTICIPATION_MODE_LABELS: Record<ParticipationMode, string> = {
  present: 'Présent',
  proxy_given: 'Représenté (pouvoir)',
  correspondence: 'Vote par correspondance',
  absent: 'Absent',
}

export const PARTICIPATION_MODE_COLORS: Record<ParticipationMode, string> = {
  present: 'green',
  proxy_given: 'blue',
  correspondence: 'purple',
  absent: 'gray',
}

export const VOTE_TYPE_LABELS: Record<VoteType, string> = {
  for: 'Pour',
  against: 'Contre',
  abstain: 'Abstention',
}

export const VOTE_TYPE_COLORS: Record<VoteType, string> = {
  for: 'green',
  against: 'red',
  abstain: 'gray',
}

export const VOTE_METHOD_LABELS: Record<VoteMethod, string> = {
  in_person: 'En présentiel',
  proxy: 'Par pouvoir',
  correspondence: 'Par correspondance',
}

export const COUNCIL_POSITION_LABELS: Record<CouncilPosition, string> = {
  PRESIDENT: 'Président',
  VICE_PRESIDENT: 'Vice-président',
  SECRETARY: 'Secrétaire',
  TREASURER: 'Trésorier',
  MEMBER: 'Membre',
}

export const CANDIDACY_STATUS_LABELS: Record<CandidacyStatus, string> = {
  pending: 'En attente',
  eligible: 'Éligible',
  ineligible: 'Non éligible',
  elected: 'Élu(e)',
  not_elected: 'Non élu(e)',
  withdrawn: 'Retirée',
}

export const MANDATE_END_REASON_LABELS: Record<MandateEndReason, string> = {
  expiration: 'Fin de mandat',
  resignation: 'Démission',
  revocation: 'Révocation',
  death: 'Décès',
  sale: 'Vente du lot',
}

export const ANNEX_TYPE_LABELS: Record<AnnexType, string> = {
  quote: 'Devis',
  contract: 'Contrat',
  report: 'Rapport',
  plan: 'Plan',
  financial: 'Document financier',
  legal: 'Document juridique',
  other: 'Autre',
}

// ===== FILTRES =====

export interface MeetingFilters {
  residence_id: string
  fiscal_year_id?: string
  status?: MeetingStatus
  meeting_type?: MeetingType
  search?: string
}

export interface AllMeetingsFilters {
  residence_id?: string
  status?: MeetingStatus
  meeting_type?: MeetingType
  search?: string
  includePast?: boolean // Si false, exclut les CLOSED
}

export interface ResolutionFilters {
  meeting_id?: string
  status?: ResolutionStatus
  resolution_category?: ResolutionCategory
  majority_type?: MajorityType
  search?: string
}

export interface CouncilMandateFilters {
  residence_id: string
  is_active?: boolean
  position?: CouncilPosition
}

// ===== TYPES POUR FORMULAIRES =====

export type MeetingFormData = Omit<MeetingInsert, 'id' | 'created_at' | 'updated_at'>
export type ResolutionFormData = Omit<ResolutionInsert, 'id' | 'created_at' | 'updated_at'>
export type MeetingParticipantFormData = Omit<MeetingParticipantInsert, 'id' | 'created_at'>
export type MeetingBureauFormData = Omit<MeetingBureauInsert, 'id' | 'created_at'>
export type OwnerVoteFormData = Omit<OwnerVoteInsert, 'id' | 'voted_at'>
export type ProxyFormData = Omit<ProxyInsert, 'id' | 'created_at' | 'updated_at'>
export type CouncilCandidacyFormData = Omit<CouncilCandidacyInsert, 'id' | 'created_at' | 'updated_at'>

// ===== TYPES POUR CALCULS AG =====

export interface VoteResult {
  resolution_id: string
  total_shares: number
  shares_for: number
  shares_against: number
  shares_abstain: number
  is_adopted: boolean
  majority_reached: boolean
  second_vote_possible: boolean // Pour art. 25-1
}

export interface QuorumInfo {
  meeting_id: string
  total_shares: number
  present_shares: number
  represented_shares: number
  correspondence_shares: number
  quorum_reached: boolean
  quorum_percentage: number
}

export interface AttendanceStats {
  total_owners: number
  present_count: number
  represented_count: number
  correspondence_count: number
  absent_count: number
  total_shares: number
  present_shares: number
  represented_shares: number
  correspondence_shares: number
}
